import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import "./index.css";
import LoginPage from "./pages/LoginPage";
import RoutingErrorPage from "./pages/RoutingErrorPage";
import RootPage from "./pages/RootPage";
import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import DashboardPage from "./pages/DashboardPage";
import StatsPage from "./pages/StatsPage";
import OrdersPage from "./pages/OrdersPage";
import TicketingPage from "./pages/TicketingPage";
import NotificationsPage from "./pages/NotificationsPage";
import ProductPage from "./pages/ProductPage";
import ContactPage from "./pages/ContactPage";
import DocumentationPage from "./pages/DocumentationPage";
import SettingsPage from "./pages/SettingsPage";
import { QueryClient, QueryClientProvider } from "react-query";
import OrdersExportsPage from "./pages/OrdersExportsPage";
import OrdersToPrintPage from "./pages/OrdersToPrintPage";
import ProductEarmoldsPage from "./pages/ProductEarmoldsPage";
import ProductMaterialsPage from "./pages/ProductMaterialsPage";
import ProductSuppliesPage from "./pages/ProductSuppliesPage";
import OrdersDeliveryNotePage from "./pages/OrdersDeliveryNotePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import NewPasswordPage from "./pages/NewPasswordPage";
import ResetPasswordDonePage from "./pages/ResetPasswordDonePage";
import NewPasswordDonePage from "./pages/NewPasswordDonePage";
import SignInPage from "./pages/SignInPage";
import OrdersDetailPage from "./pages/OrdersDetailPage";
import * as Sentry from "@sentry/react";
import InvitationPage from "./pages/InvitationPage";
import InvitationLoginPage from "./pages/InvitationLoginPage";
import OrdersNewPage from "./pages/OrdersNewPage";
import OrdersSuppliesPage from "./pages/OrdersSuppliesPage";
import TicketingCreatePage from "./pages/TicketingCreatePage";
import TicketingCreateLabelsPage from "./pages/TicketingCreateLabelsPage";
import TicketingDetailPage from "./pages/TicketingDetailPage";
import { setDefaultOptions } from "date-fns";
import { fr } from "date-fns/locale";
import ContactEnterprisesDetailPage from "./pages/ContactEnterprisesDetailPage";
import ContactCentersDetailPage from "./pages/ContactCentersDetailPage";
import ContactUsersDetailPage from "./pages/ContactUsersDetailPage";
import ProductVariantsPage from "./pages/ProductVariantsPage";
import ProductVariantsDetailPage from "./pages/ProductVariantsDetailPage";
import PatientsPage from "./pages/PatientsPage";
import PatientDetailPage from "./pages/PatientDetailPage";
import OrdersDeliveryNoteDetailPage from "./pages/OrdersDeliveryNoteDetailPage";

Sentry.init({
	dsn: "https://f710c465b73b4d554957d8cf612a80da@o4507707666726912.ingest.de.sentry.io/4507774649892944",
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration(),
	],
	// Tracing
	tracesSampleRate: 1.0, //  Capture 100% of the transactions
	// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
	tracePropagationTargets: [
		"localhost",
		"https://d3fxh6xbenvy8j.cloudfront.net",
	],
	// Session Replay
	replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const router = createBrowserRouter([
	{
		path: "/",
		element: <RootPage />,
		errorElement: <RoutingErrorPage />,
		children: [
			{
				path: "dashboard",
				element: <DashboardPage />,
			},
			{
				path: "stats",
				element: <StatsPage />,
			},
			{
				path: "orders",
				element: <OrdersPage />,
			},
			{
				path: "patients",
				element: <PatientsPage />,
			},
			{
				path: "patients/:id",
				element: <PatientDetailPage />,
			},
			{
				path: "orders/supplies",
				element: <OrdersSuppliesPage />,
			},
			{
				path: "orders/:id",
				element: <OrdersDetailPage />,
			},
			{
				path: "orders/exports",
				element: <OrdersExportsPage />,
			},
			{
				path: "orders/print",
				element: <OrdersToPrintPage />,
			},
			{
				path: "orders/deliverynote",
				element: <OrdersDeliveryNotePage />,
			},
			{
				path: "orders/deliverynote/:id",
				element: <OrdersDeliveryNoteDetailPage />,
			},
			{
				path: "tickets",
				element: <TicketingPage />,
			},
			{
				path: "tickets/:id",
				element: <TicketingDetailPage />,
			},
			{
				path: "tickets/new",
				element: <TicketingCreatePage />,
			},
			{
				path: "tickets/labels",
				element: <TicketingCreateLabelsPage />,
			},
			{
				path: "notifications",
				element: <NotificationsPage />,
			},
			{
				path: "products",
				element: <ProductPage />,
			},
			{
				path: "products/earmolds/:id",
				element: <ProductVariantsPage />,
			},
			{
				path: "products/earmolds/:id/variants/:sid",
				element: <ProductVariantsDetailPage />,
			},
			{
				path: "products/earmolds",
				element: <ProductEarmoldsPage />,
			},
			{
				path: "products/materials",
				element: <ProductMaterialsPage />,
			},
			{
				path: "products/supplies",
				element: <ProductSuppliesPage />,
			},
			{
				path: "contacts",
				element: <ContactPage />,
			},
			{
				path: "contacts/enterprises/:id",
				element: <ContactEnterprisesDetailPage />,
			},
			{
				path: "contacts/centers/:id",
				element: <ContactCentersDetailPage />,
			},
			{
				path: "contacts/users/:id",
				element: <ContactUsersDetailPage />,
			},
			{
				path: "documentation",
				element: <DocumentationPage />,
			},
			{
				path: "settings",
				element: <SettingsPage />,
			},
		], // Define here all routes which should inherits from the root sidebar
	},
	{
		path: "/orders/new",
		element: <OrdersNewPage />,
		errorElement: <RoutingErrorPage />,
	},
	{
		path: "/login",
		element: <LoginPage />,
	},
	{
		path: "/signin",
		element: <SignInPage />,
		errorElement: <RoutingErrorPage />,
	},
	{
		path: "/password/reset",
		element: <ResetPasswordPage />,
		errorElement: <RoutingErrorPage />,
	},
	{
		path: "/password/reset/done",
		element: <ResetPasswordDonePage />,
		errorElement: <RoutingErrorPage />,
	},
	{
		path: "/password/new",
		element: <NewPasswordPage />,
		errorElement: <RoutingErrorPage />,
	},
	{
		path: "/password/new/done",
		element: <NewPasswordDonePage />,
		errorElement: <RoutingErrorPage />,
	},
	{
		path: "/invitation",
		element: <InvitationPage />,
		errorElement: <RoutingErrorPage />,
	},
	{
		path: "/invitation/login",
		element: <InvitationLoginPage />,
		errorElement: <RoutingErrorPage />,
	},
]);

const theme = extendTheme({
	colors: {
		brand: {
			25: "#FAFCFF",
			35: "#FAFCFF",
			50: "#EEF5FF",
			100: "#D9E9FF",
			200: "#BCD9FF",
			300: "#8EC2FF",
			400: "#599FFF",
			500: "#1B58F5",
			600: "#1B58F5",
			700: "#1444E1",
			800: "#1737B6",
		},
		color: {
			50: "#E2E8F0",
			100: "#EEF5FF",
			200: "#BCD9FF",
			300: "#8EC2FF",
			400: "#599FFF",
			500: "#2B75FF",
			600: "#1B58F5",
			700: "#1444E1",
			800: "#1737B6",
			900: "#19338F",
			950: "#142157",
		},
		zinc: {
			50: "#FAFAFA",
		},
	},
	styles: {
		global: {
			body: {
				fontFamily: "Jakarta",
			},
		},
	},
});

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60_000, // minutes
			cacheTime: 10 * 60_000, // minutes
			refetchOnReconnect: true,
			refetchOnWindowFocus: true,
		},
		mutations: {
			onError: (err) => {
				mutateOnError(err as MutateErrors);
			},
		},
	},
});

setDefaultOptions({ locale: fr });

/**
 * I18N
 */
import "./i18n";
import { MutateErrors, mutateOnError } from "./lib/generic";

ReactDOM.createRoot(document.getElementById("root")!).render(
	<QueryClientProvider client={queryClient}>
		<ChakraProvider theme={theme}>
			<RouterProvider router={router} />
		</ChakraProvider>
	</QueryClientProvider>,
);
